import React, { useEffect, useMemo, useState, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { fmtDate, maskCpfCnpj } from "@/lib/format";
import { PageHeader, EmptyState, StatusBadge } from "@/components/Common";
import { Toolbar, TableShell, FormModal, TextField, TextArea, SelectField } from "@/components/FormKit";
import { useAuth } from "@/context/AuthContext";
import { Pencil, Ban, Loader2, Download, Upload } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  person_type: "juridica", legal_name: "", trade_name: "", document: "", phone: "",
  email: "", contact_name: "", pix_key: "", bank: "", agency: "", account: "", notes: "", status: "ativo",
};

export default function SuppliersPage() {
  const { hasPermission } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = () => { setLoading(true); api.get("/suppliers").then((r) => setRows(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (row) => { setEditing(row); setForm({ ...EMPTY, ...row }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editing) await api.put(`/suppliers/${editing.id}`, form);
      else await api.post("/suppliers", form);
      toast.success("Fornecedor salvo com sucesso.");
      setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); } finally { setSubmitting(false); }
  };

  const deactivate = async (row) => {
    if (!window.confirm(`Inativar o fornecedor ${row.legal_name}?`)) return;
    try { await api.delete(`/suppliers/${row.id}`); toast.success("Fornecedor inativado."); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const fileRef = useRef(null);
  const [importing, setImporting] = useState(false);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/suppliers/import-template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "modelo_fornecedores.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(apiError(err)); }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/suppliers/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.message || "Importação concluída.");
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setImporting(false); }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return rows.filter((r) => [r.legal_name, r.trade_name, r.document, r.email].some((v) => (v || "").toLowerCase().includes(q)));
  }, [rows, search]);

  const canEdit = hasPermission("suppliers.update");

  return (
    <div className="space-y-6">
      <PageHeader title="Fornecedores" subtitle="Cadastro de fornecedores do clube" />
      <Toolbar search={search} setSearch={setSearch} searchTestId="suppliers-search"
        onNew={hasPermission("suppliers.create") ? openNew : null} newLabel="Novo Fornecedor" newTestId="add-supplier-button">
        <button onClick={downloadTemplate} data-testid="download-template-button"
          className="border border-slate-200 text-slate-600 font-medium px-3 py-2 rounded-lg text-sm flex items-center gap-2 hover:bg-slate-50 shrink-0">
          <Download className="h-4 w-4" /> Baixar modelo
        </button>
        {hasPermission("suppliers.create") && (
          <>
            <input ref={fileRef} type="file" accept=".xlsx,.csv" onChange={handleImportFile} className="hidden" data-testid="import-file-input" />
            <button onClick={() => fileRef.current?.click()} disabled={importing} data-testid="import-suppliers-button"
              className="border border-brand text-brand font-medium px-3 py-2 rounded-lg text-sm flex items-center gap-2 hover:bg-brand-light disabled:opacity-60 shrink-0">
              {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Importar
            </button>
          </>
        )}
      </Toolbar>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-brand" /></div>
        : filtered.length === 0 ? <EmptyState text="Nenhum fornecedor cadastrado." />
        : (
        <TableShell testId="suppliers-table" headers={[
          { label: "Razão Social / Nome" }, { label: "CPF/CNPJ" }, { label: "Telefone" }, { label: "Contato" }, { label: "Status" }, { label: "Ações", align: "right" },
        ]}>
          {filtered.map((r) => (
            <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/60 transition-colors" data-testid={`supplier-row-${r.id}`}>
              <td className="px-4 py-3.5">
                <div className="font-medium text-slate-800">{r.legal_name}</div>
                {r.trade_name && <div className="text-xs text-slate-400">{r.trade_name}</div>}
              </td>
              <td className="px-4 py-3.5 text-slate-600 font-mono text-xs">{r.document || "—"}</td>
              <td className="px-4 py-3.5 text-slate-600">{r.phone || "—"}</td>
              <td className="px-4 py-3.5 text-slate-600">{r.contact_name || "—"}</td>
              <td className="px-4 py-3.5"><StatusBadge status={r.status} /></td>
              <td className="px-4 py-3.5">
                <div className="flex items-center justify-end gap-1">
                  {canEdit && <button onClick={() => openEdit(r)} data-testid={`edit-supplier-${r.id}`} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500"><Pencil className="h-4 w-4" /></button>}
                  {canEdit && r.status === "ativo" && <button onClick={() => deactivate(r)} data-testid={`deactivate-supplier-${r.id}`} className="p-1.5 rounded-md hover:bg-rose-50 text-rose-500"><Ban className="h-4 w-4" /></button>}
                </div>
              </td>
            </tr>
          ))}
        </TableShell>
      )}

      <FormModal open={open} onOpenChange={setOpen} title={editing ? "Editar Fornecedor" : "Novo Fornecedor"} onSubmit={submit} submitting={submitting} testId="supplier-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SelectField label="Tipo de Pessoa" value={form.person_type} onChange={(v) => set("person_type", v)} testId="supplier-person-type"
            options={[{ value: "fisica", label: "Pessoa Física" }, { value: "juridica", label: "Pessoa Jurídica" }]} />
          <SelectField label="Status" value={form.status} onChange={(v) => set("status", v)} testId="supplier-status"
            options={[{ value: "ativo", label: "Ativo" }, { value: "inativo", label: "Inativo" }]} />
          <TextField label={form.person_type === "fisica" ? "Nome" : "Razão Social"} value={form.legal_name} onChange={(v) => set("legal_name", v)} testId="supplier-legal-name" required className="sm:col-span-2" />
          <TextField label="Nome Fantasia" value={form.trade_name} onChange={(v) => set("trade_name", v)} testId="supplier-trade-name" />
          <TextField label="CPF/CNPJ" value={form.document} onChange={(v) => set("document", maskCpfCnpj(v))} testId="supplier-document" />
          <TextField label="Telefone" value={form.phone} onChange={(v) => set("phone", v)} testId="supplier-phone" />
          <TextField label="E-mail" type="email" value={form.email} onChange={(v) => set("email", v)} testId="supplier-email" />
          <TextField label="Nome do Contato" value={form.contact_name} onChange={(v) => set("contact_name", v)} testId="supplier-contact" />
          <TextField label="Chave PIX" value={form.pix_key} onChange={(v) => set("pix_key", v)} testId="supplier-pix" />
          <TextField label="Banco" value={form.bank} onChange={(v) => set("bank", v)} testId="supplier-bank" />
          <TextField label="Agência" value={form.agency} onChange={(v) => set("agency", v)} testId="supplier-agency" />
          <TextField label="Conta" value={form.account} onChange={(v) => set("account", v)} testId="supplier-account" />
          <TextArea label="Observações" value={form.notes} onChange={(v) => set("notes", v)} testId="supplier-notes" className="sm:col-span-2" />
        </div>
      </FormModal>
    </div>
  );
}
